/**
 * Storage adapter for zustand persist that respects functional cookie consent.
 *
 * On the .ru segment, we cannot persist user preferences (model/provider/generation
 * settings) without explicit consent (FZ-152, FZ-156). This wrapper:
 *   - reads existing values regardless (so a previously-saved choice still loads
 *     until the user explicitly opts out and we clean up);
 *   - blocks writes if functional consent is not granted;
 *   - removes (cleanup) any persisted key when called explicitly via `purgeFunctionalKeys()`.
 *
 * On .com — no gating (legacy behaviour).
 *
 * Use as: `storage: createConsentAwareJSONStorage()`
 */

import type { PersistStorage, StateStorage, StorageValue } from 'zustand/middleware';
import { createJSONStorage } from 'zustand/middleware';
import { isRuRegion } from './region';
import { getCookieConsent } from './cookieConsent';

/** Список ключей localStorage, относящихся к functional cookies. */
export const FUNCTIONAL_STORAGE_KEYS = [
  'image-storage',
  'chat-storage',
  'video-store',
  'tts-storage',
  'motion-control-storage',
  'communication-style-storage',
] as const;

const isFunctionalAllowed = (): boolean => {
  // .com — без гейтинга (старое поведение сохраняется).
  if (!isRuRegion()) return true;
  return getCookieConsent()?.functional === true;
};

/**
 * StateStorage wrapper: setItem/removeItem гейтятся по согласию,
 * getItem всегда читает (на случай если согласие было раньше — данные подхватятся,
 * пока пользователь не отозвал).
 */
const createGatedStateStorage = (): StateStorage => ({
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    if (!isFunctionalAllowed()) return;
    window.localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(name);
  },
});

/** Drop-in замена `createJSONStorage(() => localStorage)` с гейтингом. */
export const createConsentAwareJSONStorage = <T>(): PersistStorage<T> | undefined => {
  return createJSONStorage<T>(() => createGatedStateStorage()) as PersistStorage<T> | undefined;
};

/**
 * Удаляет все persisted-ключи функциональных сторов из localStorage.
 * Вызывать когда пользователь отозвал functional consent.
 */
export const purgeFunctionalStorageKeys = () => {
  if (typeof window === 'undefined') return;
  for (const key of FUNCTIONAL_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
};

// Re-export so consumers don't need a separate import line for the basic factory.
export { createJSONStorage };

// Helper kept for tests / debug.
export type { StorageValue };
